import {
  Box,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProviderCustomFields } from '../../types/practices/types';
import { isExternalProvider } from '../../utils/externalEntity';
import { isActiveProviderBridge } from '../../utils/providerBridgeUtils';
import {
  formatProviderName,
  getCustomFieldValue,
} from '../../utils/customFieldUtils';
import { DuplicateLastNamePopover } from '../provider/DuplicateLastNamePopover';
import ExternalAffordance from '../external-indicator/ExternalAffordance';
import MultiClinicBadge from '../multi-clinic-badge/MultiClinicBadge';

export type ProviderNameMode = 'inline' | 'link' | 'chip' | 'table';

interface ProviderWithDuplicates {
  duplicateProviders?: Record<string, string>;
}

export type DuplicatesBehavior = 'popover' | 'tooltip' | 'off';

export interface ProviderNameProps {
  provider: {
    id: string;
    firstname?: string;
    firstName?: string;
    lastName?: string;
    lastname?: string;
    middlename?: string | null;
    middleName?: string | null;
    suffix?: string | null;
    title?: string | null;
    gender?: 'M' | 'F' | 'O' | string | null;
    external?: boolean | null;
    isExternal?: boolean | null;
    externalTablespace?: string | null;
    customFields?: ProviderCustomFields;
    relatedPractices?:
      | Array<{
          id?: string | null;
          name: string;
          external?: boolean | null;
          isExternal?: boolean | null;
          marketExternal?: boolean | null;
          externalMarket?: boolean | null;
          externalTablespace?: string | null;
        }>
      | null;
    providerBridge?:
      | Array<{
          practiceId: string;
          endDate?: string;
          displayDate?: string;
        }>
      | null;
  };
  duplicateProviders?: Record<string, string>;
  mode?: ProviderNameMode;
  showTooltip?: boolean;
  highlightDuplicates?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  truncate?: boolean;
  duplicatesBehavior?: DuplicatesBehavior;
  onNavigate?: (id: string) => void;

  // NavigateText compatibility - for provider cases that need NavigateText styling
  useNavigateTextStyle?: boolean; // Use NavigateText's exact styling (0.75rem, 1rem, 700, text.brandLight)
  customName?: string; // Override displayed name (useful for suffixes, custom provider names)
}

/**
 * Reusable ProviderName component that handles formatting, duplicate highlighting,
 * tooltips, and gender colors across all surfaces
 */
export const ProviderName: React.FC<ProviderNameProps> = ({
  provider,
  duplicateProviders = {},
  mode = 'inline',
  showTooltip = true,
  highlightDuplicates = true,
  size = 'md',
  truncate = true,
  duplicatesBehavior = 'popover',
  onNavigate,
  useNavigateTextStyle = false,
  customName,
}) => {
  const navigate = useNavigate();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Coerce ID to string for consistent comparison
  const providerId = String(provider.id);

  // Navigation handler
  const handleNavigation = () => {
    if (onNavigate) {
      onNavigate(providerId);
    } else {
      // Always navigate to provider (since this is ProviderName)
      navigate(`/providers/${providerId}`);
    }
  };

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current)
        clearTimeout(hoverTimeoutRef.current);
      if (leaveTimeoutRef.current)
        clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    if (!isTooltipOpen) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsTooltipOpen(true);
      }, 300);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    leaveTimeoutRef.current = setTimeout(() => {
      setIsTooltipOpen(false);
    }, 1000); // 1 second delay before closing
  };

  // Helper function to create tooltip content (fallback for non-interactive contexts)
  const createTooltipContent = () => (
    <VStack
      align="start"
      spacing="0.5rem"
      maxW="18rem"
      p="0.5rem"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box>
        <Text
          fontWeight="600"
          fontSize="0.875rem"
          color="white"
          mb="0.25rem"
        >
          Alert- Similar Names Detected
        </Text>
        <Text fontSize="0.75rem" lineHeight="1.4" color="gray.200">
          Multiple providers share the last name '{lastName}'. Confirm
          by NPI/practice before scheduling.
        </Text>
      </Box>
      {otherDuplicates.length > 0 && (
        <Box w="100%">
          <Text
            fontSize="0.75rem"
            fontWeight="600"
            mb="0.375rem"
            color="white"
          >
            Other providers with same last name:
          </Text>
          <VStack align="start" spacing="0.25rem">
            {otherDuplicates.map(({ id, displayName }) => (
              <Box
                key={id}
                as="button"
                w="100%"
                textAlign="left"
                p="0.375rem 0.5rem"
                borderRadius="0.25rem"
                bg="transparent"
                border="1px solid transparent"
                fontSize="0.75rem"
                color="blue.300"
                _hover={{
                  color: 'blue.100',
                  bg: 'blue.900',
                  borderColor: 'blue.600',
                  textDecoration: 'underline',
                }}
                _focus={{
                  outline: '2px solid',
                  outlineColor: 'blue.400',
                  outlineOffset: '2px',
                }}
                _active={{
                  bg: 'blue.800',
                  transform: 'translateY(1px)',
                }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  navigate(`/providers/${id}`);
                }}
                cursor="pointer"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                transition="all 0.15s ease"
                role="link"
                tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/providers/${id}`);
                  }
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap="0.375rem"
                >
                  <Box
                    fontSize="0.625rem"
                    color="blue.400"
                    fontWeight="600"
                  >
                    →
                  </Box>
                  <Box fontWeight="500">{displayName}</Box>
                </Box>
              </Box>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );

  // Check if this provider is a duplicate
  // A provider is considered a duplicate if its ID exists in the duplicateProviders map
  // or if it has its own duplicateProviders field with entries
  const providerDuplicateProviders =
    (provider as ProviderWithDuplicates).duplicateProviders || {};
  const combinedDuplicateProviders = {
    ...duplicateProviders,
    ...providerDuplicateProviders,
  };

  // Check if current provider is a duplicate (exists in the map or has duplicates)
  const isDuplicate =
    providerId in combinedDuplicateProviders ||
    Object.keys(providerDuplicateProviders).length > 0;

  // Get provider's last name for tooltip context
  const lastName = provider.lastname || provider.lastName || '';

  // Get other duplicate providers (excluding current one)
  const otherDuplicates = Object.entries(combinedDuplicateProviders)
    .filter(([id]) => id !== providerId)
    .map(([id, displayName]) => ({ id, displayName }));

  // Color mapping - NavigateText style or gender-based
  const getColor = () => {
    // NavigateText style override
    if (useNavigateTextStyle) {
      return 'text.brandLight';
    }

    // Gender-based color for normal ProviderName
    const gender = getCustomFieldValue(
      provider.customFields,
      'Gender'
    );
    if (gender === 'Male') return 'text.brandLight';
    if (gender === 'Female') return 'text.pink500';
    return 'text.primary';
  };

  // Size-based styling - matches original formatting from QA branch
  const getSizeStyles = () => {
    // NavigateText style override
    if (useNavigateTextStyle) {
      return {
        fontSize: '0.75rem',
        lineHeight: '1rem',
        fontWeight: '700',
      };
    }

    switch (size) {
      case 'sm':
        // For small inline text (ProviderSearchSelect, tables)
        return {
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          fontWeight: '400',
        };
      case 'lg':
        // For large headers (ProviderDetail, ProviderCompactCard headers)
        return {
          fontSize: '1.5rem',
          lineHeight: '133%',
          fontWeight: '700',
        };
      case 'xl':
        // For extra large headers
        return {
          fontSize: '2.25rem',
          lineHeight: '133%',
          fontWeight: '700',
        };
      case 'md':
      default:
        // For medium text - standard body text
        return {
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          fontWeight: '500',
        };
    }
  };

  // Get the display name - formatProviderName now includes credentials from customFields
  const displayName =
    customName ||
    formatProviderName(
      {
        ...provider,
        customFields: provider.customFields,
      },
      true // includeCredentials = true
    );

  // Create the name element
  const createNameElement = () => {
    const baseStyles = {
      ...getSizeStyles(),
      color: getColor(),
      ...(!truncate
        ? {
            whiteSpace: 'normal' as const,
            wordBreak: 'break-word' as const,
          }
        : !useNavigateTextStyle
        ? {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' as const,
          }
        : {}),
      // NavigateText styling
      ...(useNavigateTextStyle && {
        whiteSpace: 'normal' as const,
        wordBreak: 'break-word' as const,
        overflow: 'visible' as const,
        textOverflow: 'unset' as const,
      }),
    };

    switch (mode) {
      case 'link':
      case 'table':
        return (
          <Text
            {...baseStyles}
            cursor="pointer"
            _hover={{
              textDecoration: 'underline',
              ...(useNavigateTextStyle && {
                color: 'text.primary',
              }),
            }}
            onClick={handleNavigation}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNavigation();
              }
            }}
            tabIndex={0}
          >
            {displayName}
          </Text>
        );
      case 'chip':
        return (
          <Text {...baseStyles} display="inline">
            {displayName}
          </Text>
        );
      case 'inline':
      default:
        return (
          <Text {...baseStyles} display="inline">
            {displayName}
          </Text>
        );
    }
  };

  // Create the duplicate chip for chip mode
  const createDuplicateChip = () => {
    if (mode !== 'chip' || !isDuplicate) return null;

    return (
      <Tag size="sm" colorScheme="yellow" variant="solid" ml="0.5rem">
        <TagLabel>⚠ Alert- Similar names</TagLabel>
      </Tag>
    );
  };

  // Create the highlight wrapper
  const createHighlightWrapper = (children: React.ReactNode) => {
    if (!isDuplicate || !highlightDuplicates) return children;

    const highlightBox = (
      <Box
        bg="yellow.100"
        px="0.25rem"
        py="0.125rem"
        borderRadius="0.25rem"
        cursor="help"
        display="inline-block"
        transition="all 0.2s ease"
        _hover={{
          bg: 'yellow.200',
          transform: 'translateY(-1px)',
          boxShadow: 'sm',
        }}
        aria-label="Alert- Similar names"
      >
        {children}
      </Box>
    );

    if (!showTooltip) return highlightBox;

    // Handle different duplicate behaviors
    switch (duplicatesBehavior) {
      case 'off':
        return highlightBox;

      case 'tooltip':
        return (
          <Tooltip
            label={createTooltipContent()}
            hasArrow
            placement="top"
            openDelay={0}
            closeDelay={0}
            portalProps={{ appendToParentPortal: true }}
            isOpen={isTooltipOpen}
            onOpen={() => setIsTooltipOpen(true)}
            onClose={() => setIsTooltipOpen(false)}
          >
            <Box
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {highlightBox}
            </Box>
          </Tooltip>
        );

      case 'popover':
      default:
        return (
          <DuplicateLastNamePopover
            lastName={lastName}
            otherDuplicates={otherDuplicates}
            onNavigate={onNavigate}
            placement="top"
          >
            {highlightBox}
          </DuplicateLastNamePopover>
        );
    }
  };

  // Render the component
  const nameElement = createNameElement();
  const duplicateChip = createDuplicateChip();
  const highlightedName = createHighlightWrapper(nameElement);

  const linkedPractices = (() => {
    const all = provider.relatedPractices ?? [];
    const bridges = provider.providerBridge ?? [];
    if (!bridges.length) return all;
    return all.filter((practice) => {
      const practiceBridges = bridges.filter((b) => b.practiceId === practice.id);
      return !practiceBridges.length || practiceBridges.some(isActiveProviderBridge);
    });
  })();
  const showExternalBadge = isExternalProvider(provider);

  return (
    <Box display="inline-flex" alignItems="center" gap="0.375rem">
      {highlightedName}
      {duplicateChip}
      {showExternalBadge ? (
        <Box flexShrink={0} aria-label="External provider">
          <ExternalAffordance entityType="provider" size="sm" />
        </Box>
      ) : null}
      <MultiClinicBadge practices={linkedPractices} />
    </Box>
  );
};

export default ProviderName;
